import dagre from 'dagre';
import {
  resolveOrgChartVisualType,
  getOwnershipEdgeStyle,
} from './orgChartStyles';
import { normalizeShareholdingPercent } from './shareholdingPercent';
import { getSideShareholderChartTheme } from './shareholderTypes';

export const COMPANY_CHART_WIDTH = 220;
export const LAYOUT_NODE_WIDTH = 220;
export const COMPANY_CHART_HEIGHT = 120;
/** Rendered company node height in React Flow (padding + wrapped title). */
export const COMPANY_NODE_RENDER_HEIGHT = 108;
export const LAYOUT_NODE_HEIGHT = 120;
export const EXTERNAL_NODE_WIDTH = 200;
export const EXTERNAL_NODE_HEIGHT = 84;
/** Horizontal gap between a company box and its side shareholder box */
export const EXTERNAL_GAP = 112;
/** Horizontal lane reserved beside a company when a third-party sits on that side */
export const EXTERNAL_SIDE_LANE = EXTERNAL_GAP + EXTERNAL_NODE_WIDTH + 32;

/**
 * Map company id → extra left/right width needed for third-party side nodes.
 */
export function getExternalPaddingByCompany(nodes, edges = []) {
  const map = new Map();

  nodes
    .filter((n) => n.type === 'externalShareholder')
    .forEach((ext) => {
      const companyId = String(
        ext.data?.companyId
        || edges.find((e) => e.source === ext.id)?.target
        || ''
      );
      if (!companyId) return;

      const entry = map.get(companyId) || { left: 0, right: 0 };
      if (ext.data?.side === 'left') {
        entry.left = Math.max(entry.left, EXTERNAL_SIDE_LANE);
      } else {
        entry.right = Math.max(entry.right, EXTERNAL_SIDE_LANE);
      }
      map.set(companyId, entry);
    });

  return map;
}

function nodeBounds(node) {
  if (node.type === 'externalShareholder') {
    return {
      left: node.position.x,
      right: node.position.x + EXTERNAL_NODE_WIDTH,
      top: node.position.y,
      bottom: node.position.y + EXTERNAL_NODE_HEIGHT,
      width: EXTERNAL_NODE_WIDTH,
      height: EXTERNAL_NODE_HEIGHT,
    };
  }
  const w = LAYOUT_NODE_WIDTH;
  const h = LAYOUT_NODE_HEIGHT;
  return {
    left: node.position.x,
    right: node.position.x + w,
    top: node.position.y,
    bottom: node.position.y + h,
    width: w,
    height: h,
  };
}

/**
 * Push same-rank nodes apart when third-party lanes still collide after layout.
 */
export function resolveChartCollisions(nodes, direction = 'TB', minGap = 20) {
  if (!nodes?.length) return nodes;

  const chartNodes = nodes.filter(
    (n) => (n.type === 'company' || n.type === 'externalShareholder') && n.position
  );
  if (chartNodes.length < 2) return nodes;

  const rankKey = (n) => (
    direction === 'LR'
      ? Math.round(n.position.y / 50)
      : Math.round(n.position.y / 50)
  );

  const ranks = new Map();
  chartNodes.forEach((n) => {
    const key = rankKey(n);
    if (!ranks.has(key)) ranks.set(key, []);
    ranks.get(key).push(n);
  });

  const shiftIds = new Set();

  const shiftNode = (node, dx) => {
    if (!node?.position || shiftIds.has(String(node.id))) return;
    node.position = { ...node.position, x: node.position.x + dx };
    shiftIds.add(String(node.id));

    if (node.type === 'company') {
      chartNodes
        .filter((n) => n.type === 'externalShareholder' && String(n.data?.companyId) === String(node.id))
        .forEach((ext) => {
          if (ext.position) {
            ext.position = { ...ext.position, x: ext.position.x + dx };
            shiftIds.add(String(ext.id));
          }
        });
    }
  };

  ranks.forEach((row) => {
    const sorted = [...row].sort((a, b) => a.position.x - b.position.x);
    let cursor = -Infinity;

    sorted.forEach((node) => {
      const bounds = nodeBounds(node);
      if (bounds.left < cursor + minGap) {
        const dx = cursor + minGap - bounds.left;
        shiftNode(node, dx);
        bounds.left += dx;
        bounds.right += dx;
      }
      cursor = bounds.right + minGap;
    });
  });

  return nodes;
}

/**
 * Place third-party shareholder nodes beside their target company after tree layout.
 */
export function positionExternalShareholdersForGroup(nodes, edges = [], direction = 'TB') {
  if (!nodes?.length) return nodes;

  const byId = new Map(nodes.map((n) => [String(n.id), n]));
  const externals = nodes.filter((n) => n.type === 'externalShareholder');
  if (!externals.length) return nodes;

  const byCompany = new Map();
  externals.forEach((ext) => {
    const companyId = String(
      ext.data?.companyId
      || edges.find((e) => e.source === ext.id)?.target
      || ''
    );
    if (!byCompany.has(companyId)) byCompany.set(companyId, []);
    byCompany.get(companyId).push(ext);
  });

  byCompany.forEach((list, companyId) => {
    const company = byId.get(String(companyId));
    if (!company?.position) return;

    const cx = company.position.x;
    const cy = company.position.y;
    const left = list.filter((n) => n.data?.side === 'left');
    const right = list.filter((n) => n.data?.side !== 'left');

    left.forEach((n, i) => {
      const stackOffset = i * (EXTERNAL_NODE_HEIGHT + 24)
        - ((left.length - 1) * (EXTERNAL_NODE_HEIGHT + 24)) / 2;
      n.position = {
        x: cx - EXTERNAL_GAP - EXTERNAL_NODE_WIDTH,
        y: cy + (COMPANY_NODE_RENDER_HEIGHT - EXTERNAL_NODE_HEIGHT) / 2 + stackOffset,
      };
    });

    right.forEach((n, i) => {
      const stackOffset = i * (EXTERNAL_NODE_HEIGHT + 24)
        - ((right.length - 1) * (EXTERNAL_NODE_HEIGHT + 24)) / 2;
      n.position = {
        x: cx + LAYOUT_NODE_WIDTH + EXTERNAL_GAP,
        y: cy + (COMPANY_NODE_RENDER_HEIGHT - EXTERNAL_NODE_HEIGHT) / 2 + stackOffset,
      };
    });
  });

  return resolveChartCollisions(nodes, direction);
}

const companyCenterX = (node) => node.position.x + LAYOUT_NODE_WIDTH / 2;

/**
 * Shift a company node and its descendants (and attached side shareholders) by dx.
 */
function shiftCompanySubtree(nodes, companyId, dx, treeChildren) {
  if (!dx || Math.abs(dx) < 0.5) return;

  const byId = new Map(nodes.map((n) => [String(n.id), n]));
  const toShift = new Set([String(companyId)]);

  const walk = (id) => {
    (treeChildren.get(id) || []).forEach((childId) => {
      if (!toShift.has(childId)) {
        toShift.add(childId);
        walk(childId);
      }
    });
  };
  walk(String(companyId));

  toShift.forEach((id) => {
    const node = byId.get(id);
    if (node?.position) {
      node.position = { ...node.position, x: node.position.x + dx };
    }
    nodes
      .filter((n) => n.type === 'externalShareholder' && String(n.data?.companyId) === id)
      .forEach((ext) => {
        if (ext.position) {
          ext.position = { ...ext.position, x: ext.position.x + dx };
        }
      });
  });
}

/**
 * For TB layouts, center each parent over its sole child so vertical edges stay straight.
 */
export function alignVerticalChainCenters(nodes, edges, direction = 'TB') {
  if (direction !== 'TB' || !nodes?.length) return nodes;

  const companyIds = new Set(
    nodes.filter((n) => n.type === 'company' && n.position).map((n) => String(n.id))
  );
  if (!companyIds.size) return nodes;

  const treeChildren = new Map();
  edges.forEach((e) => {
    if (e.targetHandle) return;
    const source = String(e.source);
    const target = String(e.target);
    if (!companyIds.has(source) || !companyIds.has(target)) return;
    if (!treeChildren.has(source)) treeChildren.set(source, []);
    treeChildren.get(source).push(target);
  });

  const hasParent = new Set([...treeChildren.values()].flat());
  const roots = [...companyIds].filter((id) => !hasParent.has(id));

  const alignFrom = (parentId) => {
    const children = (treeChildren.get(parentId) || []).filter((id) => companyIds.has(id));
    if (children.length === 1) {
      const parent = nodes.find((n) => String(n.id) === parentId);
      const child = nodes.find((n) => String(n.id) === children[0]);
      if (parent?.position && child?.position) {
        const dx = companyCenterX(parent) - companyCenterX(child);
        shiftCompanySubtree(nodes, children[0], dx, treeChildren);
      }
    }
    children.forEach((childId) => alignFrom(childId));
  };

  roots.forEach((rootId) => alignFrom(rootId));
  return nodes;
}

function mapCompanyApiNode(n, childIds) {
  const id = String(n.id);
  const typeCode = n.data?.type || n.data?.company_type_code || 'LS';
  const stake = normalizeShareholdingPercent(n.data?.stake ?? n.data?.shareholding_percentage);
  const isRootParent = !childIds.has(id);
  const chartType = resolveOrgChartVisualType({
    companyTypeCode: typeCode,
    isRootParent,
    stake,
  });

  const profile = n.data?.details || {};
  const details = {
    name: n.data?.label || n.data?.name,
    country: n.data?.country,
    type: typeCode,
    description: profile.description ?? n.data?.profile_summary ?? n.data?.profileSummary,
    founded: profile.founded ?? n.data?.founded_year ?? n.data?.foundedYear,
    employees: profile.employees ?? n.data?.employee_count ?? n.data?.employeeCount,
    ceo: profile.ceo ?? n.data?.ceo_name ?? n.data?.ceoName,
    website: profile.website ?? n.data?.website,
  };

  return {
    id,
    type: 'company',
    position: { x: 0, y: 0 },
    data: {
      label: n.data?.label || n.data?.name || `Company ${id}`,
      type: chartType,
      typeBadge: String(typeCode).toUpperCase(),
      typeCode,
      country: n.data?.country || '',
      stake,
      capital: n.data?.capital ?? profile.capital ?? n.data?.revenue ?? profile.revenue,
      revenue: n.data?.revenue ?? profile.revenue,
      shareholders: Array.isArray(n.data?.shareholders) ? n.data.shareholders : [],
      hasChildren: false,
      isExpanded: true,
      details,
    },
  };
}

function mapExternalApiNode(n) {
  return {
    id: String(n.id),
    type: 'externalShareholder',
    position: { x: 0, y: 0 },
    data: {
      label: n.data?.label || n.data?.name || 'Third party',
      subtype: n.data?.subtype || n.data?.type,
      stake: normalizeShareholdingPercent(n.data?.stake ?? n.data?.percentage),
      isGroupCompany: Boolean(n.data?.isGroupCompany),
      linkedCompanyId: n.data?.linkedCompanyId ?? null,
      side: n.data?.side || 'left',
      companyId: n.data?.companyId != null ? String(n.data.companyId) : '',
      companyName: n.data?.companyName,
      shareholderId: n.data?.shareholderId,
      nationality: n.data?.nationality,
      notes: n.data?.notes,
    },
  };
}

function mapApiEdge(e) {
  const isExternal = Boolean(e.targetHandle) || String(e.source).startsWith('ext-');
  const stake = normalizeShareholdingPercent(e.data?.stake) ?? 0;
  const sideTheme = isExternal
    ? getSideShareholderChartTheme(e.data?.isGroupCompany ? 'CORPORATE' : 'EXTERNAL')
    : null;
  const stroke = isExternal
    ? { stroke: sideTheme.edgeStroke, strokeWidth: 2, color: sideTheme.edgeMarker }
    : getOwnershipEdgeStyle(stake);

  return {
    id: e.id || `e-${e.source}-${e.target}`,
    source: String(e.source),
    target: String(e.target),
    sourceHandle: e.sourceHandle,
    targetHandle: e.targetHandle,
    type: 'ownership',
    data: { stake, isExternal, isGroupCompany: Boolean(e.data?.isGroupCompany) },
    style: {
      stroke: stroke.stroke,
      strokeWidth: stroke.strokeWidth,
      ...(isExternal ? { strokeDasharray: '6 4' } : {}),
    },
    markerEnd: { type: 'arrowclosed', color: stroke.color },
  };
}

/**
 * Convert CS Admin org-structure API payload to React Flow nodes/edges.
 * Company nodes receive an initial dagre layout; external nodes are positioned beside targets.
 */
export function buildReactFlowFromApiGraph(apiGraph) {
  if (!apiGraph?.nodes?.length) {
    return { nodes: [], edges: [] };
  }

  const edgeList = apiGraph.edges || [];
  const companyApiNodes = apiGraph.nodes.filter((n) => n.type !== 'externalShareholder');
  const externalApiNodes = apiGraph.nodes.filter((n) => n.type === 'externalShareholder');
  const treeEdges = edgeList.filter((e) => !e.targetHandle);
  const childIds = new Set(treeEdges.map((e) => String(e.target)));

  const rawCompanyNodes = companyApiNodes.map((n) => mapCompanyApiNode(n, childIds));
  const rawEdges = edgeList.map(mapApiEdge);

  const g = new dagre.graphlib.Graph();
  g.setDefaultEdgeLabel(() => ({}));
  g.setGraph({ rankdir: 'TB', nodesep: 80, ranksep: 100 });

  rawCompanyNodes.forEach((node) => {
    g.setNode(node.id, { width: COMPANY_CHART_WIDTH, height: COMPANY_CHART_HEIGHT });
  });
  treeEdges.forEach((edge) => {
    g.setEdge(String(edge.source), String(edge.target));
  });

  dagre.layout(g);

  const companyNodes = rawCompanyNodes.map((node) => {
    const pos = g.node(node.id);
    return {
      ...node,
      position: {
        x: (pos?.x || 0) - COMPANY_CHART_WIDTH / 2,
        y: (pos?.y || 0) - COMPANY_CHART_HEIGHT / 2,
      },
    };
  });

  const externalNodes = externalApiNodes.map(mapExternalApiNode);
  const nodes = positionExternalShareholdersForGroup(
    [...companyNodes, ...externalNodes],
    rawEdges,
    'TB'
  );

  return { nodes, edges: rawEdges };
}
